import React from 'react'
import { formatCurrency } from "../utils";
import styled from "styled-components";
import { Highlight } from "react-instantsearch";
import { useQuery } from "@tanstack/react-query";
import { fetchFromAPI } from "../utils";
import { convertSKUToSlug } from "../utilities";

export default function ProductBox({ hit }) {
  const gneUser = window.GNE?.user
  const { data } = useQuery({
    queryKey: ["price-tiers", hit.Id],
    queryFn: async () => {
      const result = await fetchFromAPI(`/price-tiers/${hit.Id}`);
      return result.data;
    },
    enabled: !!gneUser?.accountNumber
  });

  const {hasCampaignPricing, promoPriceMessage, textToRender, hasTieredPricing, priceDifference} = getTieredPricingDetails(data);
  const encodedSlug = encodeURIComponent(hit.Slug);
  const resolvedImagename =
    hit.ImageFilename !== ""
      ? `http://www.gnedi.com/images/thumbs/${hit.ImageFilename}`
      : "https://www.gnedi.com/images/thumbs/default-image_360.png";
  
  const discountRibbonTextClasses = ["discount-ribbon__text"];
  if (hasTieredPricing) {
    discountRibbonTextClasses.push("discount-ribbon__text--small");
  }

  return (
    <HitContainer className="item-box">
      <div className="product-item is-price-complete" data-productid="14034">
        {hasCampaignPricing && (
          <div className="product-item__discount-ribbon">
            <div className={discountRibbonTextClasses.join(" ")}>
              <div>SAVE</div>
              {hasTieredPricing && (
                <div>UP TO</div>
              )}
            </div>
            <div className="discount-ribbon__value">{formatCurrency(priceDifference)}</div>
          </div>
        )}
        <div className="ribbon-wrapper">
          <div className="picture">
            <a href={`/${encodedSlug}`} title={`Show details for ${hit.Name}`}>
                <img
                    src={resolvedImagename}
                    data-lazyloadsrc={resolvedImagename}
                    alt={hit.Name}
                    title={`Show details for ${hit.Name}`}
                />
            </a>
          </div>
          {hit.ManufacturerImage !== '' && (
            <a href={`/${encodedSlug}`} className="ribbon-position top-left">
                  <div className="product-ribbon">
                      <img src={`http://www.gnedi.com/images/${hit.ManufacturerImage}`} alt="Manufacturer Logo" height="50" />
                  </div>
              </a>
          )}
          {specialtySkus[hit.Sku.toUpperCase()] &&
            <a href={`/${encodedSlug}`} title={`Show details for ${hit.Name}`}>
              <img className="ribbon-position bottom-middle" src={specialtySkus[hit.Sku.toUpperCase()].ribbon} alt="Specialty Ribbon" />
            </a>
          }
        </div>
        <div className="details">
          <h2 className="product-title"><a href={`/${encodedSlug}`}><Highlight attribute="Name" hit={hit} /></a></h2>
          <div className="sku-item">SKU: {hit.Sku}</div>
          <PricesSection hit={hit} promoPriceMessage={promoPriceMessage} textToRender={textToRender} />
          <Buttons hit={hit} />
        </div>
      </div>
    </HitContainer>
  );
}

function PricesSection(props) {
  const gneUser = window.GNE?.user
  if (!gneUser?.accountNumber) {
    return null
  }
  const { hit, promoPriceMessage, textToRender } = props;
  
  if (specialtySkus[hit.Sku.toUpperCase()]) {
    return null
  }
  if (hit.Discontinued) {
    return (
      <div className="gneDiscontinuedMsg gneSnackBarRed product-item__snack-bar">
        <div className="sbgDescription">Discontinued</div>
      </div> 
    )
  }
  if (hit.SuggestedItemNo) {
    return (
      <div className="gneDiscontinuedMsg gneSnackBarRed product-item__snack-bar">
        <div className="sbgDescription">SKU no longer available</div>
      </div>
    )
  }
  return (
    <div className="prices">
        <em className="price old-price">MSRP: <span >{formatCurrency(hit.MSRP)}</span> / EACH </em>
        <br />
        <TieredPricing
          hit={hit}
          promoPriceMessage={promoPriceMessage}
          textToRender={textToRender}
        />
    </div>
  )
};

function Buttons(props) {
  const { hit } = props;
  const encodedSlug = encodeURIComponent(hit.Slug);
  return (
    <div className="buttons">
      {hit.Superseded
      ? (
        <a href={`/${convertSKUToSlug(hit.SuggestedItemNo)}`} className='btnPri button--small button--full-width'>View Replacement Item</a>
      )
      : (
        <a href={`/${encodedSlug}`} className='btnPri button--small button--full-width'>View Details</a>
      )}
    </div>
  )
}

function TieredPricing(props) {
  const { textToRender, promoPriceMessage } = props;
  
  if (!textToRender) {
    return <PriceSkeleton />;
  }

  return (
    <span className="price actual-price">
        {textToRender}
        {promoPriceMessage && (
          <em className="product-item__campaign-details">{promoPriceMessage}</em>
        )}
    </span>
  );
}

const specialtySkus = {
  'MODEL 470 SERIES': {
    ribbon: 'https://www.gnedi.com/images/thumbs/0295106_RIBBON_JRCO_TINERAKE_BTNSEC.png',
  },
  'JRCO MOUNT BAR': {
    ribbon: 'https://www.gnedi.com/images/thumbs/0295105_RIBBON_JRCO_MOUNTBAR_BTNSEC.png',
  },
}

const HitContainer = styled.article`
  border: 1px solid #ccc;
  border-bottom: none;
  border-left: none;
  width: 100%;
  margin: 20px 0 0;
  padding: 0 10px;

  &:hover {
  }

  .ribbon-wrapper {
    position: relative;

    .ribbon-position {
      position: absolute;

      &.top-left {
        top: 0;
        left: 0;
      }

      &.bottom-middle {
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }

  .product-ribbon {
    position: absolute;
    top: 0;
    left: 0;
  }

  .picture img {
    max-width: calc(100% - 20px);
  }

  .actual-price {
    color: var(--color--danger);
    font-weight: bold;
    height: 40px;
    display: inline-block;
  }

  .product-item__discount-ribbon {
    z-index: 1;
  }

  .buttons {
    margin-top: 10px;
  }
`;

const PriceSkeleton = styled.span`
    width: 100%;
    height: 20px;
    background-color: red;
    display: inline-block;
    animation: pulse 1.2s ease-in-out infinite;
    background: linear-gradient(-90deg, #FFB4B4 0%, #FFFFFF 50%, #FFB4B4 100%);
    background-size: 400% 400%;

    @keyframes pulse {
        0% {
        background-position: 0% 0%
        }
        100% {
        background-position: -135% 0%
        }
    }
`

function processPriceTiers(rawPriceTiers) {
  // build out price tier ranges that show N-M when a range exists, otherwise just N+
  const formattedPriceTiers = rawPriceTiers.reduce(
    (priceTiers, priceTier, index) => {
      let endQuantity = null;
      try {
        endQuantity = rawPriceTiers[index + 1].startQuantity - 1;
      } catch (e) {
        // intentionally do nothing
      }
      priceTiers.push({
        ...priceTier,
        endQuantity,
      });

      return priceTiers;
    },
    []
  );
  return formattedPriceTiers;
}

function getTieredPricingDetails(data) {
  if (!data || data.length === 0) {
    return {
      hasCampaignPricing: false,
      promoPriceMessage: null,
      textToRender: null,
      hasTieredPricing: false,
      priceDifference: 0
    }
  }
  const priceTiers = processPriceTiers(data);
  const hasCampaignPricing =
    !!priceTiers[0].campaignPrice &&
    priceTiers[0].price > priceTiers[0].campaignPrice
      ? true
      : false;

  // const $priceElement = $(this).find('.price.actual-price')
  let priceText;
  if (priceTiers.length === 1) {
    if (!hasCampaignPricing) {
      // Scenario 1: Standard item
      priceText = formatCurrency(priceTiers[0].price);
    } else {
      // Scenario 2: Standard item with campaign
      priceText = formatCurrency(priceTiers[0].campaignPrice);
    }
  }
  if (priceTiers.length > 1) {
    if (!hasCampaignPricing) {
      // Scenario 3: Standard item with multiple price tiers
      const lowestPrice = formatCurrency(
        priceTiers[priceTiers.length - 1].price
      );
      const highestPrice = formatCurrency(priceTiers[0].price);
      priceText = `${lowestPrice} - ${highestPrice}`;
    } else {
      // Scenario 4: Standard item with campaign and multiple price tiers
      const lowestPrice = formatCurrency(
        priceTiers[priceTiers.length - 1].campaignPrice
      );
      const highestPrice = formatCurrency(priceTiers[0].campaignPrice);
      priceText = `${lowestPrice} - ${highestPrice}`;
    }
  }
  const label = hasCampaignPricing ? "Now Only" : "Your Cost";
  let textToRender = `${label}: ${priceText}`;

  const endDate = new Date(priceTiers[0].endingDate); // All end dates should be the same, so we can use the 0th one
  const hasEndDate =
    priceTiers[0].endingDate &&
    priceTiers[0].endingDate !== "0001-01-01T00:00:00";
  let promoPriceMessage;
  if (hasCampaignPricing && hasEndDate) {
    const formattedDate = new Intl.DateTimeFormat().format(endDate);
    promoPriceMessage = `Limited-Time Savings Ends: ${formattedDate}`;
  }

  const hasTieredPricing = priceTiers.length > 1
  let priceDifference;
  if (hasCampaignPricing) {
    if (priceTiers.length === 1) {
      priceDifference = priceTiers[0].price - priceTiers[0].campaignPrice;
    } else {
      priceDifference = priceTiers[priceTiers.length - 1].price - priceTiers[priceTiers.length - 1].campaignPrice;
    }
  }

  return {
    hasCampaignPricing,
    promoPriceMessage,
    textToRender,
    priceDifference,
    hasTieredPricing,
  }
}
